/**
 * ImportListsModal - Import multiple lists from a MtgCraft backup
 *
 * Supports importing multiple lists at once from the main lists page.
 * Only accepts MtgCraft JSON backup format (single or multi-list).
 */
import clsx from 'clsx';
import type { ChangeEvent, ReactElement } from 'react';
import { useCallback, useMemo, useState } from 'react';
import { FaCheck, FaExclamationTriangle, FaFileImport, FaTimes } from 'react-icons/fa';
import { IoCheckmark } from 'react-icons/io5';

import Button from '@/components/Button/Button';
import Modal from '@/components/Modal/Modal';
import Spinner from '@/components/Spinner/Spinner';
import FileDropzone from '@/features/import-export/components/FileDropzone';
import ImportTabs from '@/features/import-export/components/ImportTabs';
import SupportedFormatsInfo from '@/features/import-export/components/SupportedFormatsInfo';
import type { ImportTabId } from '@/features/import-export/types';
import useCardIndex from '@/hooks/useCardIndex';
import type { MinimalCard } from '@/services/cardDatabase';

import { useAddCardToList, useCreateList } from '../hooks/api';
import { parseMultiListImport, type ParsedListCard, type ParsedListMeta } from '../utils/parsers';

// ============================================================================
// Types
// ============================================================================

interface ImportListsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface MatchedListCard {
  parsed: ParsedListCard;
  card: MinimalCard | null;
  status: 'found' | 'not-found' | 'ambiguous';
}

interface ParsedListWithMatches {
  meta: ParsedListMeta;
  matchedCards: MatchedListCard[];
  selected: boolean;
}

// ============================================================================
// Component
// ============================================================================

export default function ImportListsModal({ isOpen, onClose }: ImportListsModalProps): ReactElement {
  const [activeTab, setActiveTab] = useState<ImportTabId>('upload');
  const [text, setText] = useState('');
  const [parseErrors, setParseErrors] = useState<string[]>([]);
  const [parsedLists, setParsedLists] = useState<ParsedListWithMatches[]>([]);
  const [isImporting, setIsImporting] = useState(false);
  const [importResults, setImportResults] = useState<{ success: number; failed: number; listsCreated: number } | null>(null);

  const addCard = useAddCardToList();
  const createList = useCreateList();
  const { getCardByName, searchCardsByPrefix, loading: dbLoading } = useCardIndex();

  // Match a card against the database
  const matchCard = useCallback(
    (parsed: ParsedListCard): MatchedListCard => {
      // Try exact match first
      const exactMatch = getCardByName(parsed.name);
      if (exactMatch) {
        return { parsed, card: exactMatch, status: 'found' };
      }

      // Try fuzzy search
      const searchResults = searchCardsByPrefix(parsed.name, 5);
      if (searchResults.length === 1) {
        return { parsed, card: searchResults[0], status: 'found' };
      } else if (searchResults.length > 1) {
        const exact = searchResults.find((c) => c.name.toLowerCase() === parsed.name.toLowerCase());
        if (exact) {
          return { parsed, card: exact, status: 'found' };
        }
        return { parsed, card: searchResults[0], status: 'ambiguous' };
      }

      return { parsed, card: null, status: 'not-found' };
    },
    [getCardByName, searchCardsByPrefix],
  );

  // Process text input
  const processInput = useCallback(
    (content: string) => {
      setText(content);

      if (!content.trim()) {
        setParsedLists([]);
        setParseErrors([]);
        return;
      }

      const result = parseMultiListImport(content);
      setParseErrors(result.errors);

      // Match cards for each list
      const listsWithMatches: ParsedListWithMatches[] = result.lists.map((list) => ({
        meta: list.meta,
        matchedCards: list.cards.map(matchCard),
        selected: true, // All selected by default
      }));

      setParsedLists(listsWithMatches);
    },
    [matchCard],
  );

  // Computed totals
  const totals = useMemo(() => {
    const selectedLists = parsedLists.filter((l) => l.selected);
    const totalCards = selectedLists.reduce((sum, l) => sum + l.matchedCards.length, 0);
    const foundCards = selectedLists.reduce((sum, l) => sum + l.matchedCards.filter((c) => c.status !== 'not-found').length, 0);
    return { lists: selectedLists.length, totalCards, foundCards };
  }, [parsedLists]);

  // Handle file drop
  const handleFileDrop = useCallback(
    (file: File) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const content = e.target?.result as string;
        if (content) {
          processInput(content);
        }
      };
      reader.onerror = () => {
        setParseErrors(['Failed to read file']);
      };
      reader.readAsText(file);
    },
    [processInput],
  );

  // Handle text change
  const handleTextChange = useCallback(
    (e: ChangeEvent<HTMLTextAreaElement>) => {
      processInput(e.target.value);
    },
    [processInput],
  );

  // Toggle list selection
  const toggleListSelection = useCallback((index: number) => {
    setParsedLists((prev) => prev.map((list, i) => (i === index ? { ...list, selected: !list.selected } : list)));
  }, []);

  // Import all selected lists
  const handleImport = useCallback(async () => {
    const selectedLists = parsedLists.filter((l) => l.selected);
    if (selectedLists.length === 0) return;

    setIsImporting(true);
    let listsCreated = 0;
    let totalSuccess = 0;
    let totalFailed = 0;

    try {
      for (const list of selectedLists) {
        // Create the list
        const newListId = await createList({
          name: list.meta.name,
          icon: list.meta.icon ?? undefined,
          color: list.meta.color ?? undefined,
        });
        listsCreated++;

        // Add cards to the list
        for (const match of list.matchedCards) {
          if (match.card && match.card.oracle_id) {
            try {
              await addCard({
                list_id: newListId,
                oracle_id: match.card.oracle_id,
                name: match.card.name,
                scryfall_id: match.parsed.scryfall_id ?? match.card.id,
                quantity: match.parsed.quantity,
                priority: match.parsed.priority ?? 'medium',
                notes: match.parsed.notes ?? undefined,
              });
              totalSuccess++;
            } catch (error) {
              console.error('Failed to add card:', match.card.name, error);
              totalFailed++;
            }
          } else {
            totalFailed++;
          }
        }
      }
    } finally {
      setIsImporting(false);
    }

    setImportResults({ success: totalSuccess, failed: totalFailed, listsCreated });
  }, [parsedLists, addCard, createList]);

  const handleClose = useCallback(() => {
    setText('');
    setParseErrors([]);
    setParsedLists([]);
    setImportResults(null);
    setActiveTab('upload');
    onClose();
  }, [onClose]);

  const handleReset = useCallback(() => {
    setText('');
    setParseErrors([]);
    setParsedLists([]);
    setImportResults(null);
  }, []);

  // Upload tab content
  const uploadContent = (
    <div className="space-y-4">
      <FileDropzone onFileDrop={handleFileDrop} accept=".json" description="Supports MtgCraft backup JSON files" />
      <SupportedFormatsInfo formats={['MtgCraft Backup (single or multi-list)']} />
    </div>
  );

  // Paste tab content
  const pasteContent = (
    <div className="space-y-4">
      <textarea
        value={text}
        onChange={handleTextChange}
        placeholder="Paste your MtgCraft lists backup JSON here..."
        rows={8}
        className="border-storm bg-slate/50 text-pearl placeholder-ash focus:border-aether w-full resize-y rounded-lg border px-3 py-2 font-mono text-sm focus:outline-none"
      />
      <SupportedFormatsInfo formats={['MtgCraft Backup (single or multi-list)']} />
    </div>
  );

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Import Lists" size="lg">
      {importResults ? (
        // Results view
        <div className="space-y-4 py-4 text-center">
          <div className="flex justify-center">
            <div className="bg-success/20 flex h-16 w-16 items-center justify-center rounded-full">
              <IoCheckmark className="text-success h-8 w-8" />
            </div>
          </div>
          <div>
            <p className="text-pearl text-lg font-medium">Import Complete</p>
            <p className="text-silver mt-1">
              Created <span className="text-success font-medium">{importResults.listsCreated}</span> lists with{' '}
              <span className="text-success font-medium">{importResults.success}</span> cards
            </p>
            {importResults.failed > 0 && (
              <p className="text-danger mt-1">
                <span className="font-medium">{importResults.failed}</span> cards could not be imported
              </p>
            )}
          </div>
          <div className="flex justify-center gap-3 pt-2">
            <Button text="Import More" variant="text" onClick={handleReset} />
            <Button text="Done" onClick={handleClose} />
          </div>
        </div>
      ) : (
        // Import form
        <div className="space-y-5">
          {/* Tabs */}
          <ImportTabs activeTab={activeTab} onTabChange={setActiveTab} uploadContent={uploadContent} pasteContent={pasteContent} />

          {/* Parse errors */}
          {parseErrors.length > 0 && (
            <div className="bg-danger/10 border-danger/30 rounded-lg border p-3">
              <div className="text-danger mb-1 flex items-center gap-2 text-sm font-medium">
                <FaExclamationTriangle className="h-4 w-4" />
                <span>Import warnings</span>
              </div>
              <ul className="text-danger/80 list-inside list-disc text-xs">
                {parseErrors.slice(0, 5).map((err, i) => (
                  <li key={i}>{err}</li>
                ))}
                {parseErrors.length > 5 && <li>...and {parseErrors.length - 5} more</li>}
              </ul>
            </div>
          )}

          {/* Lists preview */}
          {parsedLists.length > 0 && (
            <div className="space-y-3">
              <div className="text-silver flex items-center justify-between text-sm font-medium">
                <span>Lists to Import ({parsedLists.length})</span>
                <span className="text-ash text-xs">
                  {totals.foundCards} of {totals.totalCards} cards found
                </span>
              </div>

              <div className="border-storm max-h-64 divide-y divide-zinc-800 overflow-y-auto rounded-lg border">
                {parsedLists.map((list, index) => {
                  const foundCount = list.matchedCards.filter((c) => c.status === 'found').length;
                  const totalCount = list.matchedCards.length;

                  return (
                    <label
                      key={index}
                      className={clsx(
                        'flex cursor-pointer items-center gap-3 px-3 py-2 transition-colors',
                        list.selected ? 'bg-slate/30' : 'hover:bg-slate/20',
                      )}
                    >
                      <input
                        type="checkbox"
                        checked={list.selected}
                        onChange={() => toggleListSelection(index)}
                        className="accent-aether h-4 w-4"
                      />
                      <span className="text-lg">{list.meta.icon || '📋'}</span>
                      <div className="min-w-0 flex-1">
                        <p className={clsx('truncate text-sm font-medium', list.selected ? 'text-pearl' : 'text-silver')}>
                          {list.meta.name}
                        </p>
                        <p className="text-ash text-xs">
                          {totalCount} cards · {foundCount} found
                        </p>
                      </div>
                      {list.meta.color && <span className="h-3 w-3 shrink-0 rounded" style={{ backgroundColor: list.meta.color }} />}
                      {foundCount === totalCount ? (
                        <FaCheck className="text-success h-4 w-4 shrink-0" />
                      ) : foundCount > 0 ? (
                        <FaExclamationTriangle className="text-gold h-4 w-4 shrink-0" />
                      ) : (
                        <FaTimes className="text-danger h-4 w-4 shrink-0" />
                      )}
                    </label>
                  );
                })}
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={handleClose}
              disabled={isImporting}
              className="text-ash hover:text-pearl px-4 py-2 text-sm transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleImport}
              disabled={isImporting || totals.lists === 0 || totals.foundCards === 0 || dbLoading}
              className={clsx(
                'flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition-colors',
                !isImporting && totals.lists > 0 && totals.foundCards > 0 && !dbLoading
                  ? 'bg-success hover:bg-success/80 text-white'
                  : 'bg-storm text-ash cursor-not-allowed',
              )}
            >
              {isImporting ? (
                <>
                  <Spinner size="sm" />
                  Importing...
                </>
              ) : (
                <>
                  <FaFileImport className="h-4 w-4" />
                  Import {totals.lists > 0 ? `${totals.lists} Lists` : 'Lists'}
                </>
              )}
            </button>
          </div>
        </div>
      )}
    </Modal>
  );
}
